import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import type { IFormElementProps, IUniversalSelectOption as IOption } from '@feathr/components';
import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

export interface IDonationFormsSelectBaseProps extends IFormElementProps {
  filters?: Record<string, unknown>;
  isLoading?: boolean;
  placeholder?: string;
}

export interface IDonationFormsSelectSingleProps extends IDonationFormsSelectBaseProps {
  isMulti?: false;
  onChange: (value?: string) => void;
  value?: string;
}

export interface IDonationFormsSelectMultiProps extends IDonationFormsSelectBaseProps {
  isMulti: true;
  onChange: (value?: string[]) => void;
  value?: string[];
}

function isMulti(
  props: Readonly<IDonationFormsSelectSingleProps | IDonationFormsSelectMultiProps>,
): props is Readonly<IDonationFormsSelectMultiProps> {
  return props.isMulti === true;
}

function DonationFormsSelect(props: Readonly<IDonationFormsSelectSingleProps>): JSX.Element;
function DonationFormsSelect(props: Readonly<IDonationFormsSelectMultiProps>): JSX.Element;
function DonationFormsSelect(
  props: Readonly<IDonationFormsSelectSingleProps | IDonationFormsSelectMultiProps>,
): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();
  const [forms, setForms] = useState<DonationForm[]>();
  const [isPending, setIsPending] = useState<boolean>(false);

  const { filters, isLoading } = props;

  useEffect(() => {
    (async function fetchForms(): Promise<void> {
      setIsPending(true);
      const fetchedForms = await DonationForms.list({ filters });
      await when(() => fetchedForms.isLoaded);

      runInAction(() => {
        setForms(fetchedForms.models);
        setIsPending(false);
      });
    })();
  }, [DonationForms, filters]);

  const options: IOption[] =
    forms?.map((form) => ({
      id: form.id,
      name: form.name,
    })) ?? [];

  if (isMulti(props)) {
    const { onChange, value, ...rest } = props;

    function handleChange(selectedOptions: IOption[]): void {
      onChange(selectedOptions?.map((o) => o.id));
    }

    const selectedOptions = options.filter((option) => value?.includes(option.id));

    return (
      <UniversalSelect<IOption>
        {...rest}
        controlPrefix={getIconForAction('search')}
        isLoading={isPending || isLoading}
        isMulti={true}
        label={rest.label ?? t('Donation forms')}
        name={'donation-forms'}
        onSelectMulti={handleChange}
        options={options}
        placeholder={rest.placeholder ?? t('All donation forms')}
        value={selectedOptions}
      />
    );
  } else {
    const { onChange, value, ...rest } = props;

    function handleChange(selectedOption: IOption): void {
      onChange(selectedOption?.id);
    }

    const selectedOption = options.find((option) => value === option.id);

    return (
      <UniversalSelect<IOption>
        {...rest}
        controlPrefix={getIconForAction('search')}
        isLoading={isPending || isLoading}
        isMulti={false}
        label={rest.label ?? t('Donation forms')}
        name={'donation-forms'}
        onSelectSingle={handleChange}
        options={options}
        placeholder={rest.placeholder ?? t('Select a donation form')}
        value={selectedOption}
      />
    );
  }
}

export default observer(DonationFormsSelect);
